// src/pages/ia-paca/metiers/ai-engineer.tsx
//
// Page métier prioritaire — AI Engineer.
//
// Stratégie SEO : capter "recrutement AI Engineer", "ai engineer
// Marseille", "ai engineer Sophia", "salaire ai engineer France 2026",
// "difference ai engineer data scientist".
//
// Différenciation : on parle praticien, on cite les outils concrets
// (LangChain, LangGraph, LlamaIndex, eval, prompt versioning).

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../../components/layout/layout';
import IaPacaHero from '../../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Code2,
  Layers,
  GitBranch,
  Sparkles,
  Brain,
} from 'lucide-react';

export default function AiEngineerPage() {
  // JSON-LD Occupation pour le SEO Google Jobs / connaissance
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: 'AI Engineer',
    description:
      "L'AI Engineer conçoit et industrialise des produits IA en production : RAG, agents, fine-tuning, intégrations LLM. Pont entre Data Science et Engineering.",
    occupationLocation: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    estimatedSalary: [
      {
        '@type': 'MonetaryAmountDistribution',
        currency: 'EUR',
        duration: 'P1Y',
        percentile10: 45000,
        median: 75000,
        percentile90: 110000,
      },
    ],
    skills:
      'Python, LangChain, LangGraph, LlamaIndex, RAG, vector databases (Pinecone, Qdrant, pgvector), LLM APIs (OpenAI, Anthropic, Mistral), prompt engineering, evaluation, MLOps basics',
    educationRequirements:
      "Master en informatique, IA, ML, ou autodidacte avec projets en production.",
    experienceRequirements: 'Junior (0-2 ans) à Senior (8+ ans).',
  };

  return (
    <Layout
      title="Recrutement AI Engineer en PACA — Marseille, Sophia, Aix | recruteurs.io"
      description="Recruter un AI Engineer en PACA : profils RAG, agents, LLM, MLOps. Cabinet de recrutement spécialisé qui pratique l'IA en production. Salaire 45-110k€. Court-list en 15 jours."
      keywords="recrutement AI Engineer, AI Engineer Marseille, AI Engineer Sophia Antipolis, salaire AI Engineer 2026, recruter ingénieur intelligence artificielle PACA, AI Engineer freelance, embaucher AI Engineer"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'AI Engineer', href: '/ia-paca/metiers/ai-engineer' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <IaPacaHero
        eyebrow="Métier IA · PACA"
        title="Recruter un"
        highlight="AI Engineer"
        subtitle="Le profil qui transforme vos POC IA en produit qui tourne en prod. Pas juste un script Jupyter, pas juste un prompt LinkedIn. Quelqu'un qui sait builder un RAG fiable, des agents qui ne hallucinent pas, et qui les met sous monitoring."
        badges={['RAG', 'Agents', 'LLM Ops', 'Stack moderne']}
      />

      {/* Back link */}
      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Tous les métiers IA
          </Link>
        </div>
      </div>

      {/* C'EST QUOI VRAIMENT */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            C'est quoi, vraiment ?
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            Un AI Engineer ≠ un Data Scientist ≠ un développeur qui a découvert ChatGPT.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              L'AI Engineer est le profil qui prend un modèle (LLM tiers comme
              GPT-4 / Claude / Mistral, ou modèle interne) et le transforme en{' '}
              <strong>produit IA fonctionnel en production</strong>. Ce n'est
              pas un chercheur. Ce n'est pas un explorateur de données. C'est
              un ingénieur qui pense déploiement, latence, coût, fiabilité,
              eval, monitoring.
            </p>
            <p>
              Apparu massivement avec l'explosion des LLM en 2023, le métier
              est devenu en 2026 le profil le plus demandé sur le marché IA
              français — et le plus pollué par les faux profils.{' '}
              <strong>
                "Faire de l'IA" ne suffit plus. Il faut savoir builder.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* TABLEAU DIFFÉRENCIATEUR — énorme valeur SEO + business */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Distinguer les profils
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            AI Engineer vs ML Engineer vs Data Scientist : la table de la vérité.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Confondre ces 3 profils, c'est garantir un raté de recrutement. Le
            jour 1 du brief technique, mettez-vous d'accord avec votre CTO.
          </p>

          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Axe</th>
                    <th className="px-4 py-3 text-violet-700">AI Engineer</th>
                    <th className="px-4 py-3">ML Engineer</th>
                    <th className="px-4 py-3">Data Scientist</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Cible principale
                    </td>
                    <td className="px-4 py-3">Produit IA en prod (LLM, RAG, agents)</td>
                    <td className="px-4 py-3">Modèle ML industrialisé</td>
                    <td className="px-4 py-3">Insight, prédiction, exploration</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Stack
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px]">
                      LangChain, LangGraph, LlamaIndex, vector DB, OpenAI API
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px]">
                      scikit-learn, PyTorch, MLflow, Vertex AI
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px]">
                      pandas, Jupyter, scikit-learn, R
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Output
                    </td>
                    <td className="px-4 py-3">Service / API, agent qui parle à des outils</td>
                    <td className="px-4 py-3">Pipeline train + serve, batch inference</td>
                    <td className="px-4 py-3">Notebook, rapport, dashboard</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Mindset
                    </td>
                    <td className="px-4 py-3">Ship it, monitor it, eval it</td>
                    <td className="px-4 py-3">Reproductibilité, performance modèle</td>
                    <td className="px-4 py-3">Exploration, hypothèses, signal</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Pour quel projet ?
                    </td>
                    <td className="px-4 py-3 text-emerald-700">
                      Chatbot RH, RAG docs internes, agent commercial
                    </td>
                    <td className="px-4 py-3">
                      Recommandation, scoring crédit, vision
                    </td>
                    <td className="px-4 py-3">
                      Étude marché, prédiction churn, segmentation
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      Salaire PACA 2026
                    </td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-violet-700">
                      55 – 105 k€
                    </td>
                    <td className="px-4 py-3 tabular-nums">52 – 100 k€</td>
                    <td className="px-4 py-3 tabular-nums">48 – 90 k€</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-violet-200 bg-violet-50/40 p-5">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-violet-700" />
              <p className="text-sm text-slate-700">
                <strong>Notre observation terrain :</strong> 70% des entreprises
                qui pensent recruter un Data Scientist devraient en fait
                recruter un AI Engineer (si l'objectif est un produit IA
                fonctionnel, pas une étude exploratoire). Cette confusion
                coûte 6 à 12 mois de retard projet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STACK ATTENDUE */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Stack technique
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            Ce qu'un AI Engineer Senior doit maîtriser en 2026.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Liste non-exhaustive. Ne demandez pas tout — demandez le bon
            sous-ensemble selon votre stack. Mais si le candidat n'en connaît
            aucun, c'est un signal fort.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            <StackBlock
              Icon={Brain}
              title="Frameworks LLM"
              items={[
                'LangChain (le standard de fait)',
                'LlamaIndex (pour les RAG complexes)',
                'LangGraph (workflows agents stateful)',
                'Pydantic AI / Instructor (outputs structurés)',
                'Semantic Kernel (Microsoft / .NET stack)',
              ]}
            />
            <StackBlock
              Icon={Layers}
              title="Vector DB & Retrieval"
              items={[
                'pgvector (le plus simple, sur PostgreSQL)',
                'Pinecone (cloud, scale)',
                'Qdrant (auto-hosted, performant)',
                'Weaviate (graphes + vecteurs)',
                'Connaître les trade-offs HNSW / IVF / dense vs sparse',
              ]}
            />
            <StackBlock
              Icon={GitBranch}
              title="Agents & Tool Use"
              items={[
                'Tool calling natif (OpenAI, Anthropic Claude)',
                'Crew AI, AutoGen, Pydantic Agents',
                'MCP (Model Context Protocol)',
                'Gestion d\'état conversationnel + mémoire long terme',
                'Anti-boucle, anti-hallucination, garde-fous',
              ]}
            />
            <StackBlock
              Icon={Code2}
              title="Production & Observabilité"
              items={[
                'Langfuse / Helicone / LangSmith (tracing LLM)',
                'Eval frameworks (Ragas, DeepEval, OpenAI Evals)',
                'Prompt versioning + A/B test prompts',
                'Cost tracking (tokens, modèles, fallback)',
                'Streaming, retries, circuit breakers',
              ]}
            />
          </div>
        </div>
      </section>

      {/* SALAIRES */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires AI Engineer en PACA — 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            Combien on paie vraiment un AI Engineer dans le sud.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Données compilées sur les 6 derniers mois. PACA est à -10 à -15%
            de Paris en moyenne, sauf Sophia Antipolis où la tension fait
            monter de +5%.
          </p>

          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-4 py-3">Séniorité</th>
                  <th className="px-4 py-3">Marseille</th>
                  <th className="px-4 py-3">Sophia Antipolis</th>
                  <th className="px-4 py-3">Aix / Nice</th>
                  <th className="px-4 py-3">Remote France</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                <tr>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    Junior (0-2 ans)
                  </td>
                  <td className="px-4 py-3 tabular-nums">45 – 55 k€</td>
                  <td className="px-4 py-3 tabular-nums">48 – 58 k€</td>
                  <td className="px-4 py-3 tabular-nums">42 – 52 k€</td>
                  <td className="px-4 py-3 tabular-nums">48 – 60 k€</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    Confirmé (3-7 ans)
                  </td>
                  <td className="px-4 py-3 tabular-nums">55 – 75 k€</td>
                  <td className="px-4 py-3 tabular-nums">58 – 80 k€</td>
                  <td className="px-4 py-3 tabular-nums">52 – 72 k€</td>
                  <td className="px-4 py-3 tabular-nums">60 – 85 k€</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    Senior (8+ ans)
                  </td>
                  <td className="px-4 py-3 tabular-nums">75 – 105 k€</td>
                  <td className="px-4 py-3 tabular-nums">80 – 115 k€</td>
                  <td className="px-4 py-3 tabular-nums">72 – 100 k€</td>
                  <td className="px-4 py-3 tabular-nums">80 – 120 k€</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    Lead / Staff
                  </td>
                  <td className="px-4 py-3 tabular-nums">100 – 140 k€</td>
                  <td className="px-4 py-3 tabular-nums">110 – 150 k€</td>
                  <td className="px-4 py-3 tabular-nums">95 – 130 k€</td>
                  <td className="px-4 py-3 tabular-nums">100 – 160 k€</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            Brut annuel hors prime / variable / BSPCE. Source : compilation
            Recruteurs.io · Hellowork · Glassdoor · Indeed · sondage candidats
            (mars 2026).
          </p>
        </div>
      </section>

      {/* COMMENT ON LES ÉVALUE */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Notre méthode de filtrage
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            5 questions qui démasquent un faux AI Engineer en 30 minutes.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Voici les questions qu'on pose en entretien technique. Vous pouvez
            les réutiliser. Si le candidat hésite ou bottle sur 3 sur 5, il
            n'est pas encore au niveau "production".
          </p>

          <ol className="mt-8 space-y-4">
            {QUESTIONS.map((q, i) => (
              <li
                key={i}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 font-mono text-sm font-bold text-violet-700">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      {q.question}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{q.answer}</p>
                    <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" />
                      Bonne réponse : {q.good}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ERREURS FRÉQUENTES */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-rose-700">
            Erreurs fréquentes
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            5 erreurs qui plombent les recrutements AI Engineer en PACA.
          </h2>

          <div className="mt-8 space-y-3">
            {ERREURS.map((e, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-rose-200 bg-white p-4"
              >
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{e.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{e.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SALARY TABLE GLOBAL référence */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Comparaison avec les autres métiers IA
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            Tous les salaires IA en PACA en un coup d'œil.
          </h2>
          <div className="mt-8">
            <IaSalaryTable compact={false} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <IaPacaCTA
        title="Vous voulez recruter un AI Engineer en PACA ?"
        subtitle="On vous fait un brief technique de 30 min avec votre CTO. Si on est aligné, on lance le sourcing dans les 48h et on vous présente 3-5 profils filtrés techniquement sous 15 jours."
      />
    </Layout>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StackBlock({
  Icon,
  title,
  items,
}: {
  Icon: typeof Brain;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-700">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
      </div>
      <ul className="mt-3 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-violet-500" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

const QUESTIONS = [
  {
    question:
      "Expliquez-moi votre dernière mise en prod LLM : retrieval, chunking, eval, monitoring.",
    answer:
      "Une réponse vague (\"j'ai utilisé OpenAI\") = signal d'alerte. On veut entendre des choix techniques motivés.",
    good:
      "Discussion de stratégie de chunking, choix d'embedding model, métrique d'eval, outil de tracing.",
  },
  {
    question: "Comment gérez-vous le prompt versioning en production ?",
    answer:
      "Si le candidat répond \"je versionne dans le code\", c'est une réponse de débutant. Un AI Engineer expérimenté utilise Langfuse, LangSmith, ou un système maison séparé du code.",
    good:
      "Outil dédié (Langfuse, PromptLayer), A/B test, rollback rapide, métriques par version.",
  },
  {
    question:
      "Un client signale que votre agent \"hallucine\" dans 5% des cas. Comment vous attaquez le problème ?",
    answer:
      "Question piège : un AI Engineer pragmatique commence par mesurer (eval set), pas par changer le modèle.",
    good:
      "Construire un eval set, identifier les patterns d'hallucination, choisir entre re-rank / few-shot / structured output / guardrails.",
  },
  {
    question:
      "Vous devez choisir entre fine-tuner un modèle open-source ou rester sur GPT-4. Comment décidez-vous ?",
    answer:
      "Pas de bonne réponse universelle, mais on attend une grille de décision (coût, latence, volumes, data privée).",
    good:
      "Analyse multi-critères : volume tokens, sensibilité data, latence requise, budget, expertise interne pour maintenir le fine-tune.",
  },
  {
    question:
      "Comment monitorez-vous les coûts LLM pour ne pas exploser le budget ?",
    answer:
      "Sujet sensible en 2026 — un AI Engineer qui n'a pas pensé coûts est un AI Engineer qui va vous ruiner.",
    good:
      "Tracking par feature/user, fallback modèles moins chers, cache de réponses, alertes seuil, choix du modèle selon contexte.",
  },
];

const ERREURS = [
  {
    title: 'Confondre AI Engineer et Data Scientist dans le brief',
    body: "Le poste est mal défini en interne, le sourcing tape à côté, et après 2 mois on se retrouve avec un profil exploration au lieu d'un profil production. Solution : aligner le brief avec votre CTO AVANT de lancer le recrutement.",
  },
  {
    title: 'Filtrer sur le mot "LangChain" dans le CV',
    body: "LangChain est cité partout en 2026, y compris par des candidats qui l'ont juste suivi 1h sur YouTube. Un test pratique (mini-RAG en 30 min) éliminera ces faux profils en une session.",
  },
  {
    title: 'Demander 5+ ans d\'expérience AI Engineer',
    body: "Le métier a explosé en 2023. Demander 5 ans, c'est demander quelqu'un qui faisait du Machine Learning avant et qui s'est reconverti. Si vous voulez un profil natif LLM, 1-3 ans de prod suffit largement.",
  },
  {
    title: 'Tester sur des questions de cours (ML théorique)',
    body: "Un AI Engineer en 2026 n'a pas besoin de connaître la backprop par cœur. Il a besoin de savoir mettre un service en prod, monitorer, debugger. Posez des questions terrain.",
  },
  {
    title: 'Ignorer la rémunération variable et les BSPCE',
    body: "Le marché AI Engineer est tendu. Si vous ne proposez pas de variable, de BSPCE, de prime de signature ou de remote partiel, vous perdez 30% des candidats au stade de l'offre.",
  },
];
